



import { Camera, Coffee, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import {
  startBreakApi,
  endBreakApi,
  getMyAttendanceApi,
} from "../app/api";
import CameraModal from "./CameraModal";

function RotatingClock({ seconds }) {
  const secondRotation = (seconds % 60) * 6;      // fast hand
  const minuteRotation = (seconds % 3600) * 0.1; // slow hand

  return (
    <div className="relative w-[90px] h-[90px] flex items-center justify-center">

      {/* SHADOW PLATE (STATIC) */}
      <div className="absolute bottom-0 w-[55px] h-[14px] bg-black/30 blur-md rounded-full" />

      {/* OUTER CLOCK BODY (STATIC) */}
      <div
        className="
          relative md:w-[60px] md:h-[60px] w-[60px] h-[60px] rounded-full
          bg-gradient-to-br from-[#c7d2fe] to-[#8b9cff]
          shadow-[0_10px_25px_rgba(0,0,0,0.35)]
          flex items-center justify-center
        "
      >
        {/* OUTER RING */}
        <div className="absolute inset-[2px] rounded-full border border-white/40" />

        {/* ================= ROTATING INNER PART ================= */}
        <div
          className="absolute inset-0 flex items-center justify-center
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${secondRotation}deg)` }}
        >
          {/* TICK MARKS */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute md:w-[2px] md:h-[6px] w-[2px] h-[6px] bg-[#4b5cff] top-[3px]"
              style={{
                transform: `rotate(${i * 30}deg)`,
                transformOrigin: "center 27px",
             
              }}
            />
          ))}
        </div>

        {/* INNER FACE */}
        <div className="absolute inset-[7px] rounded-full bg-gradient-to-br from-[#eef2ff] to-[#c7d2fe]" />

        {/* SMALL HAND (SLOW) */}
        <div
          className="absolute w-[2px] h-[14px] bg-[#4b5cff] origin-bottom bottom-1/2 rounded-full
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${minuteRotation}deg)` }}
        />

        {/* BIG HAND (FAST) */}
        <div
          className="absolute w-[3px] h-[20px] bg-[#ef4444] origin-bottom bottom-1/2 rounded-full
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${secondRotation}deg)` }}
        />

        {/* CENTER PIN */}
        <div className="absolute w-2.5 h-2.5 bg-[#1e1b4b] rounded-full z-10" />

        {/* TOP BUTTON (STATIC) */}
        <div className="absolute -top-2 w-4 h-2 bg-[#6b7cff] rounded-md shadow-md" />
      </div>
    </div>
  );
}



export default function HeroHeader() {


  const [locationError, setLocationError] = useState(false);
 
  const isMobile = window.innerWidth < 768;
  const REQUIRED_WORK_SECONDS = 9 * 60 * 60;

  const [attendance, setAttendance] = useState({ status: "NOT_PUNCHED" });
  const [workSeconds, setWorkSeconds] = useState(0);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [showPunchOutConfirm, setShowPunchOutConfirm] = useState(false);




  const workHours = String(Math.floor(workSeconds / 3600)).padStart(2, "0");
  const workMinutes = String(
    Math.floor((workSeconds % 3600) / 60)
  ).padStart(2, "0");
  const workSecs = String(workSeconds % 60).padStart(2, "0");
  

  const breakHours = String(Math.floor(breakSeconds / 3600)).padStart(2, "0");
  const breakMinutes = String(
    Math.floor((breakSeconds % 3600) / 60)
  ).padStart(2, "0");
  const breakSecs = String(breakSeconds % 60).padStart(2, "0");
  




  const [openCamera, setOpenCamera] = useState(null);
  const [location, setLocation] = useState(null);

  /* ================= DERIVED STATES ================= */
  const isPunchedIn =
    attendance.status === "IN" || attendance.status === "BREAK";
  const isOnBreak = attendance.status === "BREAK";

  /* ================= HELPERS ================= */
  const fsec = (s = 0) =>
    new Date(Math.max(s, 0) * 1000).toISOString().substr(11, 8);

  // const getLocation = () =>
  //   new Promise((resolve, reject) => {
  //     if (!navigator.geolocation) return reject("No location support");
  //     navigator.geolocation.getCurrentPosition(
  //       (pos) =>
  //         resolve({
  //           lat: pos.coords.latitude,
  //           lng: pos.coords.longitude,
  //         }),
  //       reject
  //     );
  //   });

  const getLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject("Geolocation not supported");
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,   // iOS needs slightly more time
        maximumAge: 0,
      }
    );
  });
  /* ================= LOAD ATTENDANCE (REFRESH SAFE) ================= */
  useEffect(() => {
    loadAttendance();
  }, []);

  const loadAttendance = async () => {
    const res = await getMyAttendanceApi();
    if (!res?.success || !Array.isArray(res.data)) return resetAttendance();

    const today = new Date().toLocaleDateString("en-CA");
    const a = res.data.find(
      (i) =>
        new Date(i.attendance_date).toLocaleDateString("en-CA") === today
    );

    if (!a) return resetAttendance();

    const fullDate = a.attendance_date.split("T")[0];
    const join = (d, t) => (t ? `${d}T${t}` : null);

    const punchInFull = join(fullDate, a.punch_in);
    const breakStartFull = join(fullDate, a.break_start);

    let status = "NOT_PUNCHED";
    if (a.punch_in && !a.punch_out) {
      status = a.break_start && !a.break_end ? "BREAK" : "IN";
    }
    if (a.punch_out) status = "OUT";

    /* ===== RESTORE WORK TIME ===== */
    let restoredWork = (a.total_minutes || 0) * 60;

    if ((status === "IN" || status === "BREAK") && punchInFull) {
      restoredWork += Math.floor(
        (Date.now() - new Date(punchInFull)) / 1000
      );

      if (status === "BREAK" && breakStartFull) {
        restoredWork -= Math.floor(
          (Date.now() - new Date(breakStartFull)) / 1000
        );
      }
    }

    /* ===== RESTORE BREAK TIME ===== */
    let restoredBreak = (a.break_minutes || 0) * 60;
    if (status === "BREAK" && breakStartFull) {
      restoredBreak += Math.floor(
        (Date.now() - new Date(breakStartFull)) / 1000
      );
    }

    setAttendance({ status });
    setWorkSeconds(Math.max(restoredWork, 0));
    setBreakSeconds(restoredBreak);
    setTotalBreakSeconds(restoredBreak);
  };

  const resetAttendance = () => {
    setAttendance({ status: "NOT_PUNCHED" });
    setWorkSeconds(0);
    setBreakSeconds(0);
    setTotalBreakSeconds(0);
  };

  /* ================= LIVE TIMERS ================= */
  useEffect(() => {
    if (!attendance?.status) return;

    const interval = setInterval(() => {
      if (attendance.status === "IN" || attendance.status === "BREAK") {
        setWorkSeconds((s) => s + 1);
      }
      if (attendance.status === "BREAK") {
        setBreakSeconds((s) => s + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [attendance.status]);

  /* ================= ACTIONS ================= */
  // const handlePunchIn = async () => {
  //   const loc = await getLocation();
  //   setLocation(loc);
  //   setOpenCamera("IN");
  // };
  const handlePunchIn = async () => {
    try {
      const loc = await getLocation();
      setLocation(loc);
      setOpenCamera("IN");
    } catch {
      try {
        const retryLoc = await getLocation();
        setLocation(retryLoc);
        setOpenCamera("IN");
      } catch {
        setLocationError(true);
      }
    }
  };

  // const handlePunchOut = async () => {
  //   const loc = await getLocation();
  //   setLocation(loc);
  //   setOpenCamera("OUT");
  // };


  // const handlePunchOut = async () => {
  //   try {
  //     const loc = await getLocation();
  //     setLocation(loc);
  //     setOpenCamera("OUT");
  //   } catch {
  //     setLocationError(true);
  //   }
  // };



  const handlePunchOut = async () => {
    if (workSeconds < REQUIRED_WORK_SECONDS) {
      setShowPunchOutConfirm(true);
      return;
    }
  
    try {
      const loc = await getLocation();
      setLocation(loc);
      setOpenCamera("OUT");
    } catch {
      setLocationError(true);
    }
  };



  const handleBreak = async () => {
    if (attendance.status === "BREAK") {
      const res = await endBreakApi();
      const total = (res?.data?.total_break_minutes || 0) * 60;
      setTotalBreakSeconds(total);
      setBreakSeconds(total);
      setAttendance({ status: "IN" });
    } else {
      await startBreakApi();
      setAttendance({ status: "BREAK" });
    }
  };

  /* ================= UI (UNCHANGED) ================= */
  return (
    <>
      {openCamera && (
        <CameraModal
          mode={openCamera}
          location={location}
          onClose={() => setOpenCamera(null)}
          loadAttendance={loadAttendance}
        />
      )}

      <div className="max-w-[1400px] mx-auto mt-2 px-4 sm:px-6">
        <div className="rounded-2xl px-6 pt-3 pb-16 bg-[#221054] border border-white/5 text-white shadow-2xl">




        <div className="flex flex-col  sm:flex-row justify-between gap-4">
   
        <div className="flex items-center justify-between w-full sm:w-auto gap-4">
  {/* LEFT: TITLE */}
  <div>
    <h2 className="text-xl font-semibold mt-[-24px] md:mt-[4px]">UPTASK</h2>
    <p className="text-sm opacity-70">
      Your everyday companion !!
    </p>
  </div>

  {/* RIGHT: CLOCK */}
  {/* <RotatingClock seconds={workSeconds} /> */}
  <div className="md:hidden">
  <RotatingClock seconds={workSeconds} />
</div>


</div>




          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="text-right">
              <p className="opacity-60 text-[14px] md:text-sm">Work</p>
              <p className="text-[10px] md:text-sm">{fsec(workSeconds)}</p>
            </div> 
             
            <div className="text-right">
              <p className="opacity-60 text-[14px] md:text-sm">Break</p>
              <p className="text-[10px] md:text-sm">{fsec(breakSeconds)}</p>
            </div>




<button
  onClick={isPunchedIn ? handlePunchOut : handlePunchIn}
  className={`md:px-3 md:py-1.5 px-1 py-1 rounded flex text-[11px] md:text-sm items-center gap-1 transition
    ${
      isPunchedIn
        ? "bg-red-500 hover:bg-red-600"
        : "bg-green-500 hover:bg-green-600"
    }
  `}
>
  {isPunchedIn ? (
    <>
      <LogOut size={10} /> Punch Out
    </>
  ) : (
    <>
      <Camera size={10} /> Punch In
    </>
  )}
</button>


            <button
              onClick={handleBreak}
              disabled={!isPunchedIn}
              // className={`md:px-3 md:py-1.5 px-1.5 py-1 rounded flex items-center gap-1 text-[11px] md:text-sm transition
              className={`hidden md:flex md:px-3 md:py-1.5 px-1.5 py-1 rounded items-center gap-1 text-[11px] md:text-sm transition
                ${
                  !isPunchedIn
                    ? "bg-gray-500 cursor-not-allowed"
                    : isOnBreak
                    ? "bg-blue-500 hover:scale-105"
                    : "bg-yellow-400 text-black hover:scale-105"
                }
              `}
            >
              <Coffee size={14} /> {isOnBreak ? "End Break" : "Break"}
            </button>

            
          </div>
        </div>
      </div>
      </div>


{/* ================= PUNCH OUT CONFIRM MODAL ================= */}
{showPunchOutConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
        <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
    
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Punch Out Confirmation
          </h3>
    
          <p className="mb-4 text-gray-600 text-sm leading-relaxed">
            You <strong>haven’t completed the required 9 working hours yet</strong>.
            <br />
 Do you really want to punch out?
           
          </p>
    
          <div className="flex justify-end gap-3">
            {/* CANCEL */}
            <button
              onClick={() => setShowPunchOutConfirm(false)}
              className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
    
            {/* OK */}
            <button
             onClick={async () => {
              setShowPunchOutConfirm(false);
              try {
                const loc = await getLocation();
                setLocation(loc);
                setOpenCamera("OUT");
              } catch {
                setLocationError(true);
                }
                }}


              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              OK, Punch Out
            </button>
          </div>
        </div>
      </div>
         )}

      {locationError && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl text-center max-w-sm">
      <h3 className="text-lg font-semibold mb-2">
        Location Required
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Please enable location services to Punch In.
      </p>
      <button
        onClick={() => setLocationError(false)}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
      >
        OK
      </button>
    </div>
  </div>
)}
    </>
  );
}






// import { Camera, Coffee, LogOut } from "lucide-react";
// import { useEffect, useState } from "react";
// import {
//   startBreakApi,
//   endBreakApi,
//   getMyAttendanceApi,
// } from "../app/api";
// import CameraModal from "./CameraModal";

// function RotatingClock({ seconds }) {
//   const secondRotation = (seconds % 60) * 6;      // fast hand
//   const minuteRotation = (seconds % 3600) * 0.1; // slow hand

//   return (
//     <div className="relative w-[90px] h-[90px] flex items-center justify-center">

//       {/* SHADOW PLATE (STATIC) */}
//       <div className="absolute bottom-0 w-[55px] h-[14px] bg-black/30 blur-md rounded-full" />

//       {/* OUTER CLOCK BODY (STATIC) */}
//       <div
//         className="
//           relative md:w-[60px] md:h-[60px] w-[60px] h-[60px] rounded-full
//           bg-gradient-to-br from-[#c7d2fe] to-[#8b9cff]
//           shadow-[0_10px_25px_rgba(0,0,0,0.35)]
//           flex items-center justify-center
//         "
//       >
//         {/* OUTER RING */}
//         <div className="absolute inset-[2px] rounded-full border border-white/40" />

//         {/* ================= ROTATING INNER PART ================= */}
//         <div
//           className="absolute inset-0 flex items-center justify-center
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${secondRotation}deg)` }}
//         >
//           {/* TICK MARKS */}
//           {[...Array(12)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute md:w-[2px] md:h-[6px] w-[2px] h-[6px] bg-[#4b5cff] top-[3px]"
//               style={{
//                 transform: `rotate(${i * 30}deg)`,
//                 transformOrigin: "center 27px",
             
//               }}
//             />
//           ))}
//         </div>

//         {/* INNER FACE */}
//         <div className="absolute inset-[7px] rounded-full bg-gradient-to-br from-[#eef2ff] to-[#c7d2fe]" />

//         {/* SMALL HAND (SLOW) */}
//         <div
//           className="absolute w-[2px] h-[14px] bg-[#4b5cff] origin-bottom bottom-1/2 rounded-full
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${minuteRotation}deg)` }}
//         />

//         {/* BIG HAND (FAST) */}
//         <div
//           className="absolute w-[3px] h-[20px] bg-[#ef4444] origin-bottom bottom-1/2 rounded-full
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${secondRotation}deg)` }}
//         />

//         {/* CENTER PIN */}
//         <div className="absolute w-2.5 h-2.5 bg-[#1e1b4b] rounded-full z-10" />

//         {/* TOP BUTTON (STATIC) */}
//         <div className="absolute -top-2 w-4 h-2 bg-[#6b7cff] rounded-md shadow-md" />
//       </div>
//     </div>
//   );
// }



// export default function HeroHeader() {

//   const isMobile = window.innerWidth < 768;
//   const REQUIRED_WORK_SECONDS = 9 * 60 * 60;

//   const [attendance, setAttendance] = useState({ status: "NOT_PUNCHED" });
//   const [workSeconds, setWorkSeconds] = useState(0);
//   const [breakSeconds, setBreakSeconds] = useState(0);
//   const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);





//   const workHours = String(Math.floor(workSeconds / 3600)).padStart(2, "0");
//   const workMinutes = String(
//     Math.floor((workSeconds % 3600) / 60)
//   ).padStart(2, "0");
//   const workSecs = String(workSeconds % 60).padStart(2, "0");
  

//   const breakHours = String(Math.floor(breakSeconds / 3600)).padStart(2, "0");
//   const breakMinutes = String(
//     Math.floor((breakSeconds % 3600) / 60)
//   ).padStart(2, "0");
//   const breakSecs = String(breakSeconds % 60).padStart(2, "0");
  




//   const [openCamera, setOpenCamera] = useState(null);
//   const [location, setLocation] = useState(null);

//   /* ================= DERIVED STATES ================= */
//   const isPunchedIn =
//     attendance.status === "IN" || attendance.status === "BREAK";
//   const isOnBreak = attendance.status === "BREAK";

//   /* ================= HELPERS ================= */
//   const fsec = (s = 0) =>
//     new Date(Math.max(s, 0) * 1000).toISOString().substr(11, 8);

//   // const getLocation = () =>
//   //   new Promise((resolve, reject) => {
//   //     if (!navigator.geolocation) return reject("No location support");
//   //     navigator.geolocation.getCurrentPosition(
//   //       (pos) =>
//   //         resolve({
//   //           lat: pos.coords.latitude,
//   //           lng: pos.coords.longitude,
//   //         }),
//   //       reject
//   //     );
//   //   });

//   const getLocation = () =>
//   new Promise((resolve, reject) => {
//     if (!navigator.geolocation) {
//       return reject("Geolocation not supported");
//     }

//     navigator.permissions
//       ?.query({ name: "geolocation" })
//       .then((permissionStatus) => {
//         if (permissionStatus.state === "denied") {
//           return reject("LOCATION_DENIED");
//         }

//         navigator.geolocation.getCurrentPosition(
//           (pos) => {
//             resolve({
//               lat: pos.coords.latitude,
//               lng: pos.coords.longitude,
//             });
//           },
//           (err) => {
//             reject(err.code);
//           },
//           {
//             enableHighAccuracy: true,
//             timeout: 15000,
//             maximumAge: 0,
//           }
//         );
//       })
//       .catch(() => {
//         // fallback if permissions API not supported
//         navigator.geolocation.getCurrentPosition(
//           (pos) => {
//             resolve({
//               lat: pos.coords.latitude,
//               lng: pos.coords.longitude,
//             });
//           },
//           reject,
//           {
//             enableHighAccuracy: true,
//             timeout: 15000,
//             maximumAge: 0,
//           }
//         );
//       });
//   });

//   /* ================= LOAD ATTENDANCE (REFRESH SAFE) ================= */
//   useEffect(() => {
//     loadAttendance();
//   }, []);

//   const loadAttendance = async () => {
//     const res = await getMyAttendanceApi();
//     if (!res?.success || !Array.isArray(res.data)) return resetAttendance();

//     const today = new Date().toLocaleDateString("en-CA");
//     const a = res.data.find(
//       (i) =>
//         new Date(i.attendance_date).toLocaleDateString("en-CA") === today
//     );

//     if (!a) return resetAttendance();

//     const fullDate = a.attendance_date.split("T")[0];
//     const join = (d, t) => (t ? `${d}T${t}` : null);

//     const punchInFull = join(fullDate, a.punch_in);
//     const breakStartFull = join(fullDate, a.break_start);

//     let status = "NOT_PUNCHED";
//     if (a.punch_in && !a.punch_out) {
//       status = a.break_start && !a.break_end ? "BREAK" : "IN";
//     }
//     if (a.punch_out) status = "OUT";

//     /* ===== RESTORE WORK TIME ===== */
//     let restoredWork = (a.total_minutes || 0) * 60;

//     if ((status === "IN" || status === "BREAK") && punchInFull) {
//       restoredWork += Math.floor(
//         (Date.now() - new Date(punchInFull)) / 1000
//       );

//       if (status === "BREAK" && breakStartFull) {
//         restoredWork -= Math.floor(
//           (Date.now() - new Date(breakStartFull)) / 1000
//         );
//       }
//     }

//     /* ===== RESTORE BREAK TIME ===== */
//     let restoredBreak = (a.break_minutes || 0) * 60;
//     if (status === "BREAK" && breakStartFull) {
//       restoredBreak += Math.floor(
//         (Date.now() - new Date(breakStartFull)) / 1000
//       );
//     }

//     setAttendance({ status });
//     setWorkSeconds(Math.max(restoredWork, 0));
//     setBreakSeconds(restoredBreak);
//     setTotalBreakSeconds(restoredBreak);
//   };

//   const resetAttendance = () => {
//     setAttendance({ status: "NOT_PUNCHED" });
//     setWorkSeconds(0);
//     setBreakSeconds(0);
//     setTotalBreakSeconds(0);
//   };

//   /* ================= LIVE TIMERS ================= */
//   useEffect(() => {
//     if (!attendance?.status) return;

//     const interval = setInterval(() => {
//       if (attendance.status === "IN" || attendance.status === "BREAK") {
//         setWorkSeconds((s) => s + 1);
//       }
//       if (attendance.status === "BREAK") {
//         setBreakSeconds((s) => s + 1);
//       }
//     }, 1000);

//     return () => clearInterval(interval);
//   }, [attendance.status]);

//   /* ================= ACTIONS ================= */
//   const handlePunchIn = async () => {
//     const loc = await getLocation();
//     setLocation(loc);
//     setOpenCamera("IN");
//   };

//   const handlePunchOut = async () => {
//     const loc = await getLocation();
//     setLocation(loc);
//     setOpenCamera("OUT");
//   };

//   const handleBreak = async () => {
//     if (attendance.status === "BREAK") {
//       const res = await endBreakApi();
//       const total = (res?.data?.total_break_minutes || 0) * 60;
//       setTotalBreakSeconds(total);
//       setBreakSeconds(total);
//       setAttendance({ status: "IN" });
//     } else {
//       await startBreakApi();
//       setAttendance({ status: "BREAK" });
//     }
//   };

//   /* ================= UI (UNCHANGED) ================= */
//   return (
//     <>
//       {openCamera && (
//         <CameraModal
//           mode={openCamera}
//           location={location}
//           onClose={() => setOpenCamera(null)}
//           loadAttendance={loadAttendance}
//         />
//       )}

//       <div className="max-w-[1400px] mx-auto mt-2 px-4 sm:px-6">
//         <div className="rounded-2xl px-6 pt-3 pb-16 bg-gradient-to-r from-[#2D1C6A] via-[#291857] to-[#352074] text-white">




//         <div className="flex flex-col  sm:flex-row justify-between gap-4">
   
//         <div className="flex items-center justify-between w-full sm:w-auto gap-4">
//   {/* LEFT: TITLE */}
//   <div>
//     <h2 className="text-xl font-semibold mt-[-24px] md:mt-[4px]">UPTASK</h2>
//     <p className="text-sm opacity-70">
//       Your everyday companion !!
//     </p>
//   </div>

//   {/* RIGHT: CLOCK */}
//   {/* <RotatingClock seconds={workSeconds} /> */}
//   <div className="md:hidden">
//   <RotatingClock seconds={workSeconds} />
// </div>


// </div>




//           <div className="flex flex-wrap items-center gap-2 text-sm">
//             <div className="text-right">
//               <p className="opacity-60 text-[14px] md:text-sm">Work</p>
//               <p className="text-[10px] md:text-sm">{fsec(workSeconds)}</p>
//             </div> 
             
//             <div className="text-right">
//               <p className="opacity-60 text-[14px] md:text-sm">Break</p>
//               <p className="text-[10px] md:text-sm">{fsec(breakSeconds)}</p>
//             </div>




// <button
//   onClick={isPunchedIn ? handlePunchOut : handlePunchIn}
//   className={`md:px-3 md:py-1.5 px-1 py-1 rounded flex text-[11px] md:text-sm items-center gap-1 transition
//     ${
//       isPunchedIn
//         ? "bg-red-500 hover:bg-red-600"
//         : "bg-green-500 hover:bg-green-600"
//     }
//   `}
// >
//   {isPunchedIn ? (
//     <>
//       <LogOut size={10} /> Punch Out
//     </>
//   ) : (
//     <>
//       <Camera size={10} /> Punch In
//     </>
//   )}
// </button>


//             <button
//               onClick={handleBreak}
//               disabled={!isPunchedIn}
//               className={`md:px-3 md:py-1.5 px-1.5 py-1 rounded flex items-center gap-1 text-[11px] md:text-sm transition
//                 ${
//                   !isPunchedIn
//                     ? "bg-gray-500 cursor-not-allowed"
//                     : isOnBreak
//                     ? "bg-blue-500 hover:scale-105"
//                     : "bg-yellow-400 text-black hover:scale-105"
//                 }
//               `}
//             >
//               <Coffee size={14} /> {isOnBreak ? "End Break" : "Break"}
//             </button>

            
//           </div>
//         </div>
//       </div>
//       </div>
//     </>
//   );
// }




